/**
 * 🏪 Провайдер для инициализации глобального кеша заказов
 * Заменяет QueryClientProvider из TanStack Query
 */

import React, { useEffect } from 'react'
import { useOrdersStore } from '../store/ordersStore'
import { useRealtimeSystem } from '../store/realtimeSync'
import { useAuthStore } from '../auth/store'

interface OrdersProviderProps {
  children: React.ReactNode
}

/**
 * 🚀 Компонент для инициализации системы заказов
 */
const OrdersInitializer: React.FC = () => {
  const { loadAllOrders, isInitialized, isLoading } = useOrdersStore()
  const isAuthenticated = useAuthStore(state => state.isAuthenticated())
  
  // Инициализируем реал-тайм систему
  useRealtimeSystem()
  
  // Загружаем все заказы при авторизации
  useEffect(() => {
    if (isAuthenticated && !isInitialized && !isLoading) {
      loadAllOrders()
    }
  }, [isAuthenticated, isInitialized, isLoading, loadAllOrders])
  
  return null // Этот компонент не рендерит UI
}

/**
 * 🏪 Провайдер глобального кеша заказов
 * Заменяет QueryClientProvider + RealtimeSystemProvider
 */
export const OrdersProvider: React.FC<OrdersProviderProps> = ({ children }) => {
  return (
    <>
      {/* Инициализатор системы заказов */}
      <OrdersInitializer />
      
      {/* Дочерние компоненты */}
      {children}
    </>
  )
}

/**
 * 🔄 Хук для получения состояния инициализации
 */
export function useOrdersInitialization() {
  const { isInitialized, isLoading, error } = useOrdersStore()
  const isAuthenticated = useAuthStore(state => state.isAuthenticated())
  
  return {
    isInitialized: isAuthenticated ? isInitialized : false,
    isLoading: isAuthenticated ? isLoading : false,
    error: isAuthenticated ? error : null,
    isReady: isAuthenticated && isInitialized && !isLoading && !error
  }
}
